import type { Me, Order, Pagination, SingleOrder } from '../../type';
import { deleteCall, get, patch, post } from '../axios';

import type { AppleAuthResponseDto } from './dto/apple-auth-response.dto';
import type { AppleSignUpDto } from './dto/apple-signup-request.dto';
import type { ResetPasswordDto } from './dto/reset-password.dto';
import type { UserSegmentRes } from './dto/segment.dto.res';
import type { RenewalSignUpDto } from './dto/signup.dto';
import type { UpdateUserDto } from './dto/update-user.dto';

export enum LogInMethod {
  LOCAL = 'LOCAL',
  KAKAO = 'KAKAO',
  APPLE = 'APPLE',
}

export class LogInDto {
  logInMethod?: LogInMethod;
  email?: string;
  password?: string;
  kakaoAccessToken?: string;
}

export class SignUpDto {
  name: string;
  email: string;
  password?: string;
  phone: string;
  marketingPolicy: boolean;
  kakaoId?: string;
  logInMethod?: LogInMethod;
}

interface ResetPassword {
  resetToken: string;
  newPassword: string;
}

interface DeleteAccountDto {
  deletedReason: string;
}

export const checkEmail = (email: string) => {
  return get(`users/search?email=${email}`);
};

export const login = (data: LogInDto) => {
  return post('users/login', data);
};

export const getMe = () => {
  return get<Me>('users/me');
};

export const getMyOrderHistory = (
  page = 1,
  pageSize = 10,
  unreviewedOnly = false,
) => {
  const reviewedPram = unreviewedOnly ? '&reviewed=f' : '';

  return get<Pagination<Order | SingleOrder>>(
    `users/me/order-history?page=${page}&pageSize=${pageSize}${reviewedPram}`,
  );
};

export const resetPassword = (data: ResetPassword) => {
  return post('/users/reset-password', data);
};

interface CheckReferralCodeResDto {
  success: boolean; // ex) true
  message: string; // ex) "유효한 추천인 코드입니다."
  discount: number; // ex) 2000
}
export const checkReferralCode = (referralCode: string) => {
  return get<CheckReferralCodeResDto>(
    `users/check?referralCode=${referralCode}`,
  );
};

export const deleteMyAccount = (data: DeleteAccountDto) => {
  return deleteCall('users/my-account', data);
};

/** 휴대폰 인증번호 전송 */
export const sendValidation = (phone: string) => {
  return post('users/send-validation', { phone });
};

/** 휴대폰 인증번호 확인 */
export const confirmValidation = (phone: string, code: string) => {
  return post('users/confirm-validation', { phone, code });
};

/** 이메일 찾기 */
export const searchEmails = (phone: string, phoneValidationToken: string) => {
  return post('users/search/email', { phone, phoneValidationToken });
};

/** 비밀번호 재설정을 위한 인증번호 전송 */
export const sendResetValidation = (email: string, phone: string) => {
  return post('users/v2/send-reset', { email, phone });
};

/** 비밀번호 재설정 */
export const resetPasswordByValidation = (data: ResetPasswordDto) => {
  return post('users/v2/reset-password', data);
};

/** 회원가입 */
export const signUpUser = (data: RenewalSignUpDto) => {
  return post('users/sign-up', data);
};

/** 프로필 수정 */
export const updateProfile = (data: UpdateUserDto) => {
  return patch('users/me', data);
};

/** 휴대폰 번호로 이메일 검색 */
export const checkPhone = (phone: string) => {
  return get(`users/search?phone=${phone}`);
};

export const getAppleIdToken = async (code: string) => {
  const { data } = await post<AppleAuthResponseDto>('auth/apple/token', {
    code,
  });
  return data;
};

/** 애플 로그인 */
export const appleLogin = async (appleIdToken: string) => {
  const { data } = await post<{ token: string }>('auth/apple/log-in', {
    appleIdToken,
  });
  return data;
};

/** 애플 회원가입 */
export const appleSignUp = async (appleSignUpDto: AppleSignUpDto) => {
  const { data } = await post<{ token: string }>(
    'auth/apple/sign-up',
    appleSignUpDto,
  );
  return data;
};

// 적립금 자동차감 ON
export const checkAutoUsePoint = async () => {
  const { data } = await post<{ id: number; usePoint: true }>(
    'users/me/usePoint',
    '',
  );
  return data;
};

// 적립금 자동차감 OFF
export const uncheckAutoUsePoint = async () => {
  const { data } = await deleteCall<{ id: number; usePoint: false }>(
    'users/me/usePoint',
  );
  return data;
};

// 쿠폰 자동사용 ON
export const checkAutoUseCoupon = async () => {
  const { data } = await post<{ id: number; useCoupon: true }>(
    'users/me/useCoupon',
    '',
  );
  return data;
};

// 쿠폰 자동사용 OFF
export const uncheckAutoUseCoupon = async () => {
  const { data } = await deleteCall<{ id: number; useCoupon: false }>(
    'users/me/useCoupon',
  );
  return data;
};

// 세그먼트 조회
export const getUserSegment = async () => {
  const { data } = await get<UserSegmentRes>('users/me/segment');
  return data;
};
